import React, { useState, useEffect } from 'react';
import { Activity, AlertTriangle, CheckCircle, Info, RefreshCw } from 'lucide-react';
import { getGoogleAdsClient, getDateRange } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type CampaignHealthIssue } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

export default function CampaignHealthMonitorPage() {
  const [issues, setIssues] = useState<CampaignHealthIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => { loadHealth(); }, []);

  const loadHealth = async () => {
    try {
      setLoading(true);
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (!mappings) { toast.error(`No account connected for ${company}`); return; }

      const client = getGoogleAdsClient(mappings.customer_id);
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(mappings.customer_id, getDateRange(30));
      setIssues(report.campaignHealthIssues);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8"><div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-blue-500" /><span className="ml-3">Loading...</span></div></div>;
  }

  const critical = issues.filter(i => i.severity === 'critical').length;
  const warnings = issues.filter(i => i.severity === 'warning').length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-end mb-4">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <h1 className="text-3xl font-bold mb-2">Campaign Health Monitor</h1>
      <p className="text-gray-600 mb-8">AI-powered campaign health analysis</p>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Critical Issues</span><AlertTriangle className="h-5 w-5 text-red-500" /></div>
          <div className="text-3xl font-bold text-red-600">{critical}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Warnings</span><Info className="h-5 w-5 text-yellow-500" /></div>
          <div className="text-3xl font-bold text-yellow-600">{warnings}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Total Issues</span><Activity className="h-5 w-5 text-blue-500" /></div>
          <div className="text-3xl font-bold">{issues.length}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Health Issues ({issues.length})</h2></div>
        <div className="divide-y">
          {issues.map((issue, idx) => (
            <div key={idx} className={`p-6 ${issue.severity === 'critical' ? 'bg-red-50' : issue.severity === 'warning' ? 'bg-yellow-50' : 'bg-blue-50'}`}>
              <div className="flex items-start gap-4">
                {issue.severity === 'critical' && <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />}
                {issue.severity === 'warning' && <Info className="h-6 w-6 text-yellow-600 flex-shrink-0" />}
                {issue.severity === 'info' && <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{issue.message}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${issue.severity === 'critical' ? 'bg-red-100 text-red-800' : issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                      {issue.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{issue.recommendation}</p>
                  <div className="flex gap-4 text-sm text-gray-600">
                    <span>Campaigns: {issue.affectedCampaigns.join(', ')}</span>
                    <span>Impact: {issue.estimatedImpact}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {issues.length === 0 && <div className="p-12 text-center text-gray-500"><CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-400" /><p>All campaigns healthy!</p></div>}
      </div>
    </div>
  );
}
